import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight, ChevronRight, Search, MapPin, Sparkles, ShieldCheck, Check, Star, Eye } from "lucide-react";
import { api } from "@/lib/api";
import RotatingTestimonial from "@/components/RotatingTestimonial";
import { categoryPath } from "@/lib/paths";
import ListingCard from "@/components/ListingCard";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { useSeoPage } from "@/lib/seoPages";
import { RfqStrip } from "@/components/SupplierFilterPanel";
import CategoryRFQModal from "@/components/CategoryRFQModal";
import CategoryGrid from "@/components/CategoryGrid";

function NotifyForm() {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("buyer");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/subscribe", { email, role });
      setDone(true);
      toast.success("You're on the list");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="bg-white p-6 border border-[#264839] flex items-center gap-3" data-testid="notify-done">
        <Check className="w-5 h-5 text-[#264839]" />
        <div>
          <div className="font-medium text-[#11211A]">You're on the list.</div>
          <div className="text-xs text-[#7D8C85] mt-1">We'll email you when new categories launch.</div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white p-6 border border-[#E8ECE9]" data-testid="notify-form">
      <div className="flex gap-1 text-xs mb-4">
        <button type="button" onClick={() => setRole("buyer")} className={`px-3 py-1 border ${role === "buyer" ? "border-[#264839] text-[#264839]" : "border-[#C4CFC9] text-[#7D8C85]"}`} data-testid="notify-role-buyer">I'm a Buyer</button>
        <button type="button" onClick={() => setRole("advertiser")} className={`px-3 py-1 border ${role === "advertiser" ? "border-[#264839] text-[#264839]" : "border-[#C4CFC9] text-[#7D8C85]"}`} data-testid="notify-role-advertiser">I'm a Supplier</button>
      </div>
      <input
        required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="you@company.com"
        className="w-full px-3 py-2.5 border border-[#C4CFC9] rounded-sm"
        data-testid="notify-email"
      />
      <button type="submit" disabled={busy} className="btn-primary w-full justify-center mt-3 disabled:opacity-50" data-testid="notify-submit">
        {busy ? "Adding…" : "Notify me"}
      </button>
    </form>
  );
}

const HERO_BUYER_IMAGE = "https://images.pexels.com/photos/29870245/pexels-photo-29870245.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const HERO_SUPPLIER_IMAGE = "/api/files/legacy/bath-category-hero.jpg";

export default function HomePage() {
  const seoPage = useSeoPage("home");
  const [tree, setTree] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [trending, setTrending] = useState([]);
  const [rotation, setRotation] = useState([]);
  const [q, setQ] = useState("");
  const [rfqOpen, setRfqOpen] = useState(false);
  const [bannerTop, setBannerTop] = useState([]);
  const [bannerIdx, setBannerIdx] = useState(0);
  // Category-aware banner: every parent category that currently has active suppliers; rotates when there is more than one.
  const activeCats = tree.filter((c) => (c.active_supplier_count || 0) > 0).sort((a, b) => (b.active_supplier_count || 0) - (a.active_supplier_count || 0));
  const bannerCat = activeCats.length ? activeCats[bannerIdx % activeCats.length] : (tree.find((c) => /linen/i.test(c.name)) || null);

  useEffect(() => {
    if (activeCats.length < 2 || rfqOpen) return;
    const iv = setInterval(() => setBannerIdx((i) => (i + 1) % activeCats.length), 7000);
    return () => clearInterval(iv);
  }, [activeCats.length, rfqOpen]);

  useEffect(() => {
    if (!bannerCat) return;
    const param = bannerCat.parent_id === null ? "parent_category_id" : "category_id";
    api.get(`/listings?${param}=${bannerCat.id}&limit=4`).then(({ data }) => setBannerTop(data.items || [])).catch(() => {});
  }, [bannerCat?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const trackBanner = (kind) => {
    if (!bannerCat) return;
    api.post("/events/rfq-banner", { kind, category_id: bannerCat.id, category_name: bannerCat.name, placement: "homepage" }).catch(() => {});
  };
  const openBannerRfq = () => { trackBanner("click"); setRfqOpen(true); };

  useEffect(() => {
    api.get("/categories/tree").then(({ data }) => setTree(data));
    api.get("/listings/featured?limit=6").then(({ data }) => setFeatured(data));
    api.get("/listings/trending?limit=6").then(({ data }) => setTrending(data)).catch(() => {});
    api.get("/homepage-rotation?limit=3").then(({ data }) => setRotation(data)).catch(() => {});
  }, []);

  // Auto-rotate case studies every 6 seconds


  return (
    <div data-testid="home-page">
      <SEO
        title={seoPage?.title || ""}
        description={seoPage?.description || "The B2B directory for accommodation suppliers — hotels, resorts and hospitality buyers meet verified linen, furniture, amenities and services providers. Founding cohort now open."}
        path="/"
      />
      {/* HERO — full-width split: buyers left, suppliers right (stacked on mobile, buyers first) */}
      <section className="grid grid-cols-1 md:grid-cols-2 w-full min-h-[560px] lg:min-h-[640px]" data-testid="hero-split">
        {[
          {
            key: "buyers", testid: "hero-buyers", image: HERO_BUYER_IMAGE,
            overline: "For hotels & accommodation buyers",
            headline: "Get supplier quotes for your property, fast.",
            sub: "Request quotes from trusted hospitality suppliers. Free for buyers, always.",
            subTestId: "hero-supplier-count",
            cta: "Browse Suppliers", to: "/for-hotels", ctaCls: "bg-white text-[#11211A] hover:bg-[#F0EFEA]",
          },
          {
            key: "suppliers", testid: "hero-suppliers", image: HERO_SUPPLIER_IMAGE,
            overline: "For suppliers",
            headline: "Win hotel contracts without cold calling.",
            sub: "Get in front of procurement teams looking for additional quotes in your category.",
            cta: "Become a Supplier", to: "/register?role=advertiser", ctaCls: "bg-[#B35C4A] text-white hover:bg-[#994A3B] shadow-lg shadow-[#B35C4A]/20",
          },
        ].map((h) => (
          <div key={h.key} className="relative overflow-hidden bg-[#11211A] text-white flex min-w-0 md:w-full" data-testid={h.testid}>
            <div className="absolute inset-0 bg-cover bg-center transition-transform duration-[1200ms] hover:scale-[1.03]" style={{ backgroundImage: `url('${h.image}')` }} aria-hidden="true" />
            <div className="absolute inset-0 bg-[#11211A]/75" aria-hidden="true" data-testid={`${h.testid}-overlay`} />
            <div className="relative flex flex-col justify-center w-full px-6 sm:px-10 md:px-8 lg:px-14 xl:px-20 py-20 lg:py-28">
              <div className="tracking-overline text-[#D27C6A] mb-5 fade-up">{h.overline}</div>
              {/* One H1 per page: the buyers panel (optionally overridden in Admin → SEO); the suppliers panel is an identically styled H2. */}
              {h.key === "buyers"
                ? <h1 className="font-serif font-normal text-4xl md:text-4xl lg:text-5xl xl:text-6xl leading-[1.05] tracking-tight max-w-xl fade-up fade-up-delay-1" data-testid={`${h.testid}-headline`}>{seoPage?.heading || h.headline}</h1>
                : <h2 className="font-serif font-normal text-4xl md:text-4xl lg:text-5xl xl:text-6xl leading-[1.05] tracking-tight max-w-xl fade-up fade-up-delay-1" data-testid={`${h.testid}-headline`}>{h.headline}</h2>}
              <p className="text-base md:text-lg text-white/80 mt-6 max-w-lg leading-relaxed fade-up fade-up-delay-2" data-testid={`${h.testid}-subhead`}>{h.sub}</p>
              <div className="mt-10 fade-up fade-up-delay-3">
                <Link to={h.to} className={`inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-medium tracking-tight transition-colors ${h.ctaCls}`} data-testid={`${h.testid}-cta`}>
                  {h.cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* BROWSE BY CATEGORY — slim chip strip between the hero and the RFQ banner */}
      <section className="bg-white border-b border-[#E8ECE9] py-10 sm:py-12" data-testid="section-home-category-strip">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-baseline justify-between gap-4 mb-5 sm:mb-6">
            <h2 className="font-serif text-2xl sm:text-3xl text-[#11211A] tracking-tight" data-testid="home-category-strip-label">Browse by category</h2>
            <Link to="/categories" className="hidden sm:inline-flex items-center gap-1 text-sm text-[#264839] hover:underline underline-offset-4" data-testid="home-category-strip-all">All categories <ChevronRight className="w-4 h-4" /></Link>
          </div>
          {/* all eight at once: 4 per row on desktop, 2 per row on tablet, stacked on mobile — no sideways scrolling */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="home-category-strip">
            {tree.map((cat) => {
              const open = cat.launch_status === "active" || cat.launch_status === "launching";
              return (
                <Link
                  key={cat.id}
                  to={categoryPath(cat)}
                  className={`group flex items-center justify-between gap-3 rounded-xl px-5 py-4 min-h-[64px] text-left transition-[background-color,border-color,color,transform] duration-200 hover:-translate-y-0.5 ${open ? "bg-[#264839] text-white hover:bg-[#1B3329]" : "border-2 border-[#C4CFC9] text-[#4A5550] hover:border-[#264839] hover:text-[#264839]"}`}
                  data-testid={`home-category-chip-${cat.slug || cat.id}`}
                  data-state={open ? "open" : "soon"}
                >
                  <span className="text-sm sm:text-base font-medium leading-snug">{cat.name}</span>
                  {open
                    ? <ArrowRight className="w-4 h-4 shrink-0 opacity-80 group-hover:translate-x-0.5 transition-transform" />
                    : <span className="shrink-0 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#7D8C85] bg-[#F0EFEA] rounded-full px-2.5 py-1" data-testid={`home-category-chip-soon-${cat.slug || cat.id}`}>Soon</span>}
                </Link>
              );
            })}
          </div>
          <div className="mt-5 sm:mt-6 flex sm:justify-end">
            <button
              type="button"
              onClick={() => document.getElementById("explore-categories")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-[#B35C4A] hover:bg-[#9A4C3C] text-white px-6 py-3.5 text-sm sm:text-base font-semibold tracking-tight transition-colors shadow-md shadow-[#B35C4A]/20"
              data-testid="home-category-strip-see-all"
            >
              See all 8 categories <ArrowDown className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* HOMEPAGE RFQ BANNER — directly below the hero; same component as the category pages */}
      <section className="bg-[#F0EFEA] border-b border-[#E8ECE9] py-8" data-testid="section-home-rfq">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div key={bannerCat?.id || "none"} className="animate-in fade-in duration-500" data-testid="home-rfq-banner-wrap" data-category={bannerCat?.name || ""}>
            <RfqStrip
              categoryName={bannerCat?.name || "Linen, Textiles & Soft Furnishings"}
              headline={`Send one RFQ to up to 4 ${bannerCat?.name || "Linen, Textiles & Soft Furnishings"} suppliers`}
              onClick={openBannerRfq}
              disabled={!bannerCat}
              testPrefix="home"
              className=""
              note="More categories launching soon."
            />
          </div>
          {activeCats.length > 1 && (
            <div className="mt-3 flex items-center gap-2" data-testid="home-rfq-banner-dots">
              {activeCats.map((c, i) => (
                <button key={c.id} type="button" onClick={() => setBannerIdx(i)} aria-label={`Show ${c.name}`} className={`h-1.5 rounded-full transition-all ${i === bannerIdx % activeCats.length ? "w-6 bg-[#264839]" : "w-1.5 bg-[#C4CFC9] hover:bg-[#7D8C85]"}`} data-testid={`home-rfq-banner-dot-${i}`} />
              ))}
            </div>
          )}
        </div>
      </section>

      {rfqOpen && bannerCat && (
        <CategoryRFQModal
          open={rfqOpen}
          onClose={() => setRfqOpen(false)}
          category={bannerCat}
          topListings={bannerTop.slice(0, 4)}
          onStarted={() => trackBanner("started")}
          onSubmitted={() => trackBanner("submitted")}
        />
      )}

      {/* CATEGORY LEADER SPOTLIGHT — rotates 3 random Leader listings on each page load */}
      {rotation.length > 0 && (
        <section className="bg-[#F0EFEA] border-t border-b border-[#E8ECE9] py-14" data-testid="section-leader-rotation">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-end justify-between mb-8">
              <div>
                <div className="tracking-overline mb-2 text-[#B35C4A] flex items-center gap-2">
                  <Star className="w-3.5 h-3.5 fill-current" /> Category Leader Spotlight
                </div>
                <h2 className="font-serif text-2xl md:text-3xl text-[#11211A] tracking-tight">Fresh picks, rotated every visit</h2>
                <p className="text-sm text-[#4A5550] mt-2 max-w-lg">
                  Three verified Category Leader suppliers, rotated on every page load so every visit surfaces someone new.
                </p>
              </div>
              <Link to="/search" className="mt-4 sm:mt-0 inline-flex items-center justify-center gap-2 bg-[#264839] text-white px-7 py-3.5 text-base font-medium tracking-tight hover:bg-[#1c3529] transition-colors shadow-md shadow-[#264839]/20 group" data-testid="leader-rotation-browse-all">
                Browse all suppliers <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {rotation.map((l) => (
                <ListingCard key={l.id} item={l} spotlight onSpotlightClick={() => api.post("/track/spotlight-click", { listing_id: l.id, device: window.matchMedia("(max-width: 767px)").matches ? "mobile" : "desktop" }).catch(() => {})} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MOST VIEWED THIS MONTH — Founding Suppliers only, ranked by real buyer profile views */}
      {trending.length > 0 && (
        <section className="bg-white border-b border-[#E8ECE9] py-14" data-testid="section-trending">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-end justify-between mb-8">
              <div>
                <div className="tracking-overline mb-2 text-[#B8860B] flex items-center gap-2">
                  <Eye className="w-3.5 h-3.5" /> Most viewed this month
                </div>
                <h2 className="font-serif text-2xl md:text-3xl text-[#11211A] tracking-tight">Suppliers buyers are looking at right now</h2>
                <p className="text-sm text-[#4A5550] mt-2 max-w-lg">
                  Ranked by real profile views from hotel buyers this month. Founding Suppliers only.
                </p>
              </div>
              <Link to="/pricing" className="text-sm text-[#264839] hover:underline flex items-center gap-1" data-testid="trending-become-founding-link">
                Get your company here <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5" data-testid="trending-grid">
              {trending.map((l, i) => (
                <div key={l.id} className="relative">
                  <div className="absolute -top-3 -left-2 z-10 w-8 h-8 bg-[#11211A] text-white font-serif text-sm flex items-center justify-center shadow" data-testid={`trending-rank-${l.id}`}>{i + 1}</div>
                  <ListingCard item={l} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PARENT CATEGORIES */}
      {/* SECTION 2 — HOW IT WORKS */}
      <section className="bg-white py-24" data-testid="section-how-it-works">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <div className="tracking-overline mb-3">For hoteliers</div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#11211A] tracking-tight">How it works</h2>
            <p className="text-sm text-[#7D8C85] mt-4">Purpose-built for the whole accommodation industry:</p>
            <div className="flex flex-wrap gap-2 mt-3" data-testid="hero-personas">
              {["Hotels","Motels","Serviced Apartments","Resorts","Holiday Parks"].map((p) => (
                <span key={p} className="text-xs bg-[#F0EFEA] text-[#264839] px-3 py-1.5 border border-[#264839]/15" data-testid={`persona-${p.toLowerCase().replace(/ /g,'-')}`}>{p}</span>
              ))}
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-10 lg:gap-16">
            {[
              { n: "01", t: "Post what you need", b: "Share specs, quantities, delivery location, and timeline." },
              { n: "02", t: "Get matched supplier quotes", b: "Relevant suppliers respond with pricing, lead times, and options." },
              { n: "03", t: "Compare and purchase", b: "Choose based on cost, quality, and delivery speed." },
            ].map((s) => (
              <div key={s.n} data-testid={`how-step-${s.n}`}>
                <div className="font-serif text-5xl text-[#B35C4A] leading-none mb-6">{s.n}</div>
                <div className="font-medium text-lg text-[#11211A] mb-2">{s.t}</div>
                <p className="text-sm text-[#4A5550] leading-relaxed">{s.b}</p>
              </div>
            ))}
          </div>
          <RotatingTestimonial page="home" className="mt-16" />
          <div className="mt-14">
            <Link to="/categories" className="inline-flex items-center gap-2 bg-[#264839] hover:bg-[#1B3329] text-white px-7 py-3.5 text-sm font-medium tracking-tight transition-colors" data-testid="how-cta">
              Post an RFQ (Free) <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 3 — EXPLORE SUPPLIER CATEGORIES */}
      <section id="explore-categories" className="bg-[#F0EFEA] py-24 scroll-mt-20" data-testid="section-current-focus">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <div className="tracking-overline mb-3">Categories</div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#11211A] tracking-tight" data-testid="home-categories-heading">Explore Supplier Categories</h2>
            <p className="text-base md:text-lg text-[#4A5550] mt-5 leading-relaxed" data-testid="home-categories-subline">
              Your accommodation procurement network, growing category by category.
            </p>
          </div>
          <CategoryGrid categories={tree} testPrefix="home-category-grid" />
        </div>
      </section>

      {/* SECTION 4 — SUPPLIER PITCH */}
      <section className="bg-[#11211A] text-white py-24" data-testid="section-supplier-pitch">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="tracking-overline text-[#D27C6A] mb-3">For suppliers</div>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight tracking-tight" data-testid="supplier-headline">
              For suppliers: pay to receive qualified hotel RFQs
            </h2>
            <p className="text-base text-white/80 mt-6 leading-relaxed">
              Get inbound quote requests from accommodation buyers actively sourcing products. You respond with your best offer. Win the order.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-10">
              <Link to="/pricing" className="inline-flex items-center justify-center gap-2 bg-[#B35C4A] hover:bg-[#994A3B] text-white px-8 py-4 text-base font-medium tracking-tight transition-colors shadow-lg shadow-[#B35C4A]/20" data-testid="supplier-cta">
                Become a Supplier <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/register?role=advertiser&plan=free" className="inline-flex items-center justify-center gap-2 border border-white/40 hover:border-white text-white px-8 py-4 text-base font-medium tracking-tight transition-colors" data-testid="supplier-free-listing-cta">
                List your company free
              </Link>
            </div>

            {/* Founding cohort lock-in message (replaces the slots-remaining counter) */}
            <div className="mt-10" data-testid="founding-lock-in-message">
              <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border-l-4 border-[#B8860B] px-5 py-3">
                <Star className="w-4 h-4 text-[#F0C866] fill-current shrink-0" />
                <span className="text-sm text-white/90">Become a Founding Supplier and lock in your rate for 12 months. Price increases as the platform grows.</span>
              </div>
            </div>

            {/* Public Launch Offer checklist — moved below the fold from the hero */}
            <div className="mt-8 border-l-2 border-[#D27C6A] pl-5" data-testid="launch-offer-strip">
              <div className="tracking-overline text-[#D27C6A] mb-3">Public Launch Offer</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-white/90 text-sm">
                {["Founding Supplier Badge", "60 Days Featured Placement", "Locked-in Launch Pricing"].map((b) => (
                  <div key={b} className="flex items-center gap-2" data-testid="launch-offer-item">
                    <Check className="w-4 h-4 text-[#D27C6A] shrink-0" />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <ul className="space-y-5">
            {[
              "Only relevant RFQs (category and region matched)",
              "No scraping, no spam lists",
              "Your listing doubles as a product showroom",
            ].map((b) => (
              <li key={b} className="flex items-start gap-4 border-t border-white/10 pt-5 first:border-t-0 first:pt-0" data-testid="supplier-bullet">
                <Check className="w-5 h-5 text-[#D27C6A] shrink-0 mt-0.5" />
                <span className="text-white/90 text-base leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SECTION 5 — TRUST & PROOF */}
      <section className="bg-white py-24" data-testid="section-trust">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <div className="tracking-overline mb-3">Built for procurement</div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#11211A] tracking-tight">Built for real hotel purchasing</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: <Sparkles className="w-5 h-5" />, t: "RFQ specs capture", b: "Sizes, GSM, fabric, quantities, timelines — all in one form." },
              { icon: <Check className="w-5 h-5" />,    t: "Standardized quote comparison", b: "Every supplier response formatted the same way for easy comparison." },
              { icon: <ShieldCheck className="w-5 h-5" />, t: "Claimed, accountable suppliers", b: "Every Verified Supplier has claimed and confirmed their listing, so the contact details and products you see are theirs." },
            ].map((c) => (
              <div key={c.t} className="border border-[#E8ECE9] p-8" data-testid="trust-card">
                <div className="w-10 h-10 flex items-center justify-center bg-[#F0EFEA] text-[#264839] mb-5">{c.icon}</div>
                <div className="font-medium text-lg text-[#11211A] mb-2">{c.t}</div>
                <p className="text-sm text-[#4A5550] leading-relaxed">{c.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — PROCUREMENT BRIEF (newsletter) */}
      <section id="notify" className="bg-[#F0EFEA] py-24" data-testid="section-procurement-brief">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="tracking-overline mb-3">Newsletter</div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#11211A] tracking-tight">The Procurement Brief</h2>
            <p className="text-base text-[#4A5550] mt-5 leading-relaxed">
              Join hotel procurement teams getting benchmarks, RFQ tips, and supplier alerts.
            </p>
          </div>
          <div>
            <NotifyForm />
          </div>
        </div>
      </section>

    </div>
  );
}
